export const MEASUREMENT_ID = "G-35EC73ETRZ";
// Set to the Google Ads tag ID ("AW-...") to send conversions directly;
// empty means conversions reach Google Ads only through the GA4 link.
export const ADS_ID = "";
export const INSTALL_EVENT = "extension_install";
export const STORE_CLICK_EVENT = "extension_store_click";
const PUBLIC_PATHS = new Set([
  "/",
  "/pricing",
  "/transparency",
  "/supported-sites",
  "/developers",
  "/privacy",
  "/terms",
  "/methodology",
  "/bounty",
  "/welcome",
  "/extension",
]);
// Ad click identifiers and campaign tags only; they identify the ad, not the person.
const CAMPAIGN_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
// Opt-in consent regions: advertising storage stays off; no banner is shown.
export const CONSENT_REQUIRED_REGIONS = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO",
  "GB",
  "CH",
];
const INSTALL_TRACKED_KEY = "stylome.installTracked";
type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  "ga-disable-G-35EC73ETRZ"?: boolean;
};
let started = false;
let lastPage: string | null = null;

export function publicAnalyticsPath(route: string): string | null {
  const pathname = route.split(/[?#]/)[0].replace(/\/$/, "") || "/";
  return PUBLIC_PATHS.has(pathname) ? pathname : null;
}

export function campaignQuery(search: string): string {
  const source = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const name of CAMPAIGN_PARAMS) {
    const value = source.get(name);
    if (value && value.length <= 200) kept.set(name, value);
  }
  const query = kept.toString();
  return query ? `?${query}` : "";
}

export function analyticsReferrer(value: string): string {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.origin + "/" : "";
  } catch {
    return "";
  }
}

function start(w: AnalyticsWindow) {
  w.dataLayer = w.dataLayer || [];
  w.gtag = function () {
    // Google's command queue requires an Arguments object.
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };
  w.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    region: CONSENT_REQUIRED_REGIONS,
  });
  w.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  w.gtag("js", new Date());
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
  started = true;
}

export function trackPage(route: string, search = "") {
  if (location.hostname !== "stylome.io") return;
  const w = window as AnalyticsWindow;
  const pathname = publicAnalyticsPath(route);
  const allowed = pathname !== null;
  w[`ga-disable-${MEASUREMENT_ID}`] = !allowed;
  if (!allowed) {
    lastPage = null;
    return;
  }
  if (!started) start(w);
  if (lastPage === pathname) return;
  // Explicit safe fields also apply to automatic session/engagement events.
  const fields = {
    page_location: `https://stylome.io${pathname}${campaignQuery(search)}`,
    page_title:
      pathname === "/" ? "Stylome | Scan" : `Stylome | ${pathname.slice(1)}`,
    page_referrer: analyticsReferrer(document.referrer),
    send_page_view: false,
    allow_google_signals: false,
  };
  w.gtag!("config", MEASUREMENT_ID, fields);
  if (ADS_ID) w.gtag!("config", ADS_ID);
  w.gtag!("event", "page_view", { ...fields, send_to: MEASUREMENT_ID });
  lastPage = pathname;
}

export function trackEvent(name: string, params: Record<string, string> = {}) {
  const w = window as AnalyticsWindow;
  if (!started || !w.gtag || w[`ga-disable-${MEASUREMENT_ID}`]) return;
  w.gtag("event", name, params);
}

// The extension opens /welcome?installed=1 once, on first install.
export function trackInstallOnce(search: string) {
  if (new URLSearchParams(search).get("installed") !== "1") return;
  history.replaceState(history.state, "", location.pathname);
  try {
    if (localStorage.getItem(INSTALL_TRACKED_KEY)) return;
    localStorage.setItem(INSTALL_TRACKED_KEY, "1");
  } catch {
    /* Storage can be unavailable; the event is still sent once per load. */
  }
  trackEvent(INSTALL_EVENT, { browser: "chrome" });
}
